import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getEmailBranding,
  putEmailBranding,
  listNotificationEmailTemplates,
  getNotificationEmailTemplate,
  putNotificationEmailTemplate,
  patchNotificationEmailTemplateEmailEnabled,
  deleteNotificationEmailTemplate,
  previewNotificationEmailTemplate
} from '../../services/cms';
import { uploadFileAndGetUrl } from '../../services/coa';
import RichTextEditor, { exportEmailEditorHtml } from '../../components/ui/RichTextEditor';

const emptyFooterItem = () => ({ label: '', url: '', order: 0 });

/** Fields the email engine replaces in the body (`{{key}}` in saved HTML). */
const EMAIL_BODY_INSERT_FIELDS = [
  { key: 'titleHtml', label: 'Notification title (heading)' },
  { key: 'recipientName', label: 'Recipient name' },
  { key: 'customerName', label: 'Customer name' },
  { key: 'bodyHtml', label: 'Notification message' },
  { key: 'actionButtonBlock', label: 'Action / link button' },
  { key: 'licenceRefNumber', label: 'Licence reference number' },
  { key: 'quotationRefNumber', label: 'Quotation reference number' },
  { key: 'coaName', label: 'COA name' },
  { key: 'supportEmail', label: 'Support email' },
  { key: 'privacyPolicyUrl', label: 'Privacy policy URL' },
  { key: 'clientUrl', label: 'App website URL' }
];

/** Layout blocks that match sent-email styling (green box, notice, etc.). */
const EMAIL_BODY_INSERT_BLOCKS = [
  {
    key: 'notice-box',
    label: 'Notice / warning box',
    html: '<div class="notice"><p><strong>Important:</strong> Type your notice here…</p></div>'
  }
];

const SUBJECT_INSERT_FIELDS = [
  { key: 'title', label: 'Notification title' },
  { key: 'recipientName', label: 'Recipient name' },
  { key: 'customerName', label: 'Customer name' },
  { key: 'licenceRefNumber', label: 'Licence reference' },
  { key: 'quotationRefNumber', label: 'Quotation reference' },
  { key: 'coaName', label: 'COA name' }
];

const COMPLIANCE_FOOTER_INSERT_FIELDS = [
  { key: 'privacyPolicyUrl', label: 'Privacy policy URL (from server settings)' },
  { key: 'vatNumber', label: 'VAT number (from server settings)' },
  { key: 'companyRegistrationNo', label: 'Company registration number' },
  { key: 'supportEmail', label: 'Support email' }
];

/** Normalize to #rrggbb for <input type="color"> (falls back if invalid). */
function toColorInputValue(raw) {
  const s = String(raw || '').trim();
  const bare = s.startsWith('#') ? s.slice(1) : s;
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(bare)) return '#000000';
  if (bare.length === 3) {
    const expanded = bare
      .split('')
      .map((c) => c + c)
      .join('');
    return `#${expanded.toLowerCase()}`;
  }
  return `#${bare.toLowerCase()}`;
}

const EmailTemplatesTab = () => {
  const [section, setSection] = useState('branding');

  const [branding, setBranding] = useState({
    logoUrl: '',
    brandGradientStart: '#002147',
    brandGradientEnd: '#0d1d60',
    buttonBg: '#40c191',
    buttonText: '#ffffff',
    contentHeadingColor: '#002147',
    accentBorderColor: '#40c191',
    footerItems: [],
    extraFooterHtml: '',
    complianceFooterHtml: '',
    defaultComplianceFooterHtml: ''
  });
  const [brandingLoading, setBrandingLoading] = useState(true);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const [templateList, setTemplateList] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [defaultInner, setDefaultInner] = useState('');
  const [defaultSubject, setDefaultSubject] = useState('');
  const [suggestedInner, setSuggestedInner] = useState('');
  const [innerHtml, setInnerHtml] = useState('');
  const [subjectTpl, setSubjectTpl] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [includeActionInPreview, setIncludeActionInPreview] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [emailEnabledSaving, setEmailEnabledSaving] = useState(false);

  const loadBranding = useCallback(async () => {
    try {
      setBrandingLoading(true);
      const res = await getEmailBranding();
      const d = res?.data || res;
      if (d) {
        setBranding((prev) => ({
          ...prev,
          ...d,
          footerItems: Array.isArray(d.footerItems) ? d.footerItems : []
        }));
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to load email branding');
    } finally {
      setBrandingLoading(false);
    }
  }, []);

  const loadTemplateList = useCallback(async () => {
    try {
      const res = await listNotificationEmailTemplates();
      const rows = res?.data || [];
      setTemplateList(rows);
      setSelectedKey((prev) => prev || (rows[0]?.key || ''));
    } catch (e) {
      toast.error(e?.message || 'Failed to load template list');
    }
  }, []);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  useEffect(() => {
    if (section === 'templates') {
      loadTemplateList();
    }
  }, [section, loadTemplateList]);

  useEffect(() => {
    if (section !== 'templates' || !selectedKey) return;
    const row = templateList.find((r) => r.key === selectedKey);
    if (row) setEmailEnabled(row.emailEnabled !== false);
  }, [section, selectedKey, templateList]);

  useEffect(() => {
    if (section !== 'templates') return;
    if (!selectedKey) return;
    let cancelled = false;
    (async () => {
      try {
        setTemplateLoading(true);
        const res = await getNotificationEmailTemplate(selectedKey);
        const d = res?.data;
        if (!d || cancelled) return;
        setDefaultInner(d.defaultInnerHtmlTemplate || '');
        setDefaultSubject(d.defaultSubjectTemplate || '');
        setSuggestedInner(d.suggestedInnerHtmlTemplate || d.defaultInnerHtmlTemplate || '');
        setInnerHtml(d.innerHtmlTemplate || d.defaultInnerHtmlTemplate || '');
        setSubjectTpl(d.subjectTemplate || d.defaultSubjectTemplate || '');
        setPreviewHtml(d.previewHtml || '');
        setPreviewSubject(d.previewSubject || '');
        setEmailEnabled(d.emailEnabled !== false);
      } catch (e) {
        if (!cancelled) toast.error(e?.message || 'Failed to load template');
      } finally {
        if (!cancelled) setTemplateLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [section, selectedKey]);

  const refreshPreview = useCallback(async () => {
    if (!selectedKey || templateLoading) return;
    try {
      const isCustomerEmail = !String(selectedKey).startsWith('admin:');
      const body = {
        templateKey: selectedKey,
        editorMode: 'advanced',
        innerHtmlTemplate: exportEmailEditorHtml(innerHtml),
        subjectTemplate: subjectTpl,
        includeActionButton: includeActionInPreview,
        isCustomerEmail
      };
      const res = await previewNotificationEmailTemplate(body);
      const d = res?.data;
      setPreviewHtml(d?.html || '');
      setPreviewSubject(d?.subject || '');
    } catch (e) {
      console.error(e);
    }
  }, [innerHtml, subjectTpl, includeActionInPreview, selectedKey, templateLoading]);

  useEffect(() => {
    if (section !== 'templates' || templateLoading || !selectedKey) return;
    const t = setTimeout(() => {
      refreshPreview();
    }, 500);
    return () => clearTimeout(t);
  }, [section, templateLoading, selectedKey, refreshPreview]);

  const saveBranding = async () => {
    try {
      setBrandingSaving(true);
      const { defaultComplianceFooterHtml: _def, ...brandingPayload } = branding;
      await putEmailBranding(brandingPayload);
      toast.success('Email branding saved');
      await loadBranding();
    } catch (e) {
      toast.error(e?.message || 'Failed to save branding');
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setLogoUploading(true);
      const url = await uploadFileAndGetUrl(file);
      setBranding((b) => ({ ...b, logoUrl: url }));
      toast.success('Logo uploaded — click Save branding to apply');
    } catch (err) {
      toast.error(err?.message || 'Logo upload failed');
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  };

  const loadGenericDefaultIntoEditor = () => {
    setSubjectTpl(defaultSubject);
    setInnerHtml(defaultInner);
    toast.success('Loaded generic platform default (same for all notification types)');
  };

  const loadStarterForNotificationType = () => {
    const starter = (suggestedInner || defaultInner).trim() || defaultInner;
    setInnerHtml(starter);
    toast.success('Loaded starter template for this notification category (edit freely)');
  };

  const saveTemplate = async () => {
    if (!selectedKey) return;
    try {
      setTemplateSaving(true);
      const payload = {
        editorMode: 'advanced',
        innerHtmlTemplate: exportEmailEditorHtml(innerHtml),
        subjectTemplate: subjectTpl
      };
      await putNotificationEmailTemplate(selectedKey, payload);
      toast.success('Template saved');
      await loadTemplateList();
      const res = await getNotificationEmailTemplate(selectedKey);
      const d = res?.data;
      if (d) {
        setPreviewHtml(d.previewHtml || '');
        setPreviewSubject(d.previewSubject || '');
        setInnerHtml(d.innerHtmlTemplate || d.defaultInnerHtmlTemplate || '');
        setSubjectTpl(d.subjectTemplate || d.defaultSubjectTemplate || '');
        setSuggestedInner(d.suggestedInnerHtmlTemplate || d.defaultInnerHtmlTemplate || '');
      }
    } catch (e) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setTemplateSaving(false);
    }
  };

  const resetTemplate = async () => {
    if (!selectedKey) return;
    if (!window.confirm('Remove the saved template for this notification type? The editor will load the category starter until you save again.')) return;
    try {
      await deleteNotificationEmailTemplate(selectedKey);
      toast.success('Reset to default');
      await loadTemplateList();
      const res = await getNotificationEmailTemplate(selectedKey);
      const d = res?.data;
      if (d) {
        setDefaultInner(d.defaultInnerHtmlTemplate || '');
        setDefaultSubject(d.defaultSubjectTemplate || '');
        setSuggestedInner(d.suggestedInnerHtmlTemplate || d.defaultInnerHtmlTemplate || '');
        setInnerHtml(d.innerHtmlTemplate || d.suggestedInnerHtmlTemplate || d.defaultInnerHtmlTemplate || '');
        setSubjectTpl(d.subjectTemplate || d.defaultSubjectTemplate || '');
        setPreviewHtml(d.previewHtml || '');
        setPreviewSubject(d.previewSubject || '');
      }
    } catch (e) {
      toast.error(e?.message || 'Reset failed');
    }
  };

  const toggleEmailEnabledForSelected = async () => {
    if (!selectedKey) return;
    const next = !emailEnabled;
    try {
      setEmailEnabledSaving(true);
      await patchNotificationEmailTemplateEmailEnabled(selectedKey, { emailEnabled: next });
      setEmailEnabled(next);
      setTemplateList((list) =>
        list.map((row) => (row.key === selectedKey ? { ...row, emailEnabled: next } : row))
      );
      toast.success(next ? 'Email notifications enabled for this type' : 'Email notifications disabled for this type');
    } catch (e) {
      toast.error(e?.message || 'Failed to update email setting');
    } finally {
      setEmailEnabledSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        <button
          type="button"
          onClick={() => setSection('branding')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            section === 'branding' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Branding &amp; footer
        </button>
        <button
          type="button"
          onClick={() => setSection('templates')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            section === 'templates' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Notification email templates
        </button>
        <button
          type="button"
          onClick={() => setSection('reference-guide')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            section === 'reference-guide' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Reference Guide
        </button>
      </div>

      {section === 'branding' && (
        <div className="max-w-3xl space-y-6">
          {brandingLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                <input
                  type="url"
                  value={branding.logoUrl || ''}
                  onChange={(e) => setBranding({ ...branding, logoUrl: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="https://… (or upload below)"
                />
                <div className="mt-2 flex items-center gap-3">
                  <label className="text-sm text-emerald-700 font-medium cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoFile} disabled={logoUploading} />
                    <span className="underline">{logoUploading ? 'Uploading…' : 'Upload image'}</span>
                  </label>
                  {branding.logoUrl ? (
                    <img src={branding.logoUrl} alt="Logo preview" className="h-10 object-contain border rounded" />
                  ) : null}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  ['brandGradientStart', 'Header gradient start'],
                  ['brandGradientEnd', 'Header gradient end'],
                  ['buttonBg', 'Primary button background'],
                  ['buttonText', 'Primary button text'],
                  ['contentHeadingColor', 'Heading colour'],
                  ['accentBorderColor', 'Accent / body block border']
                ].map(([field, label]) => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        aria-label={`${label} picker`}
                        title={label}
                        value={toColorInputValue(branding[field])}
                        onChange={(e) =>
                          setBranding({
                            ...branding,
                            [field]: e.target.value.toLowerCase()
                          })
                        }
                        className="h-15 w-15 shrink-0 cursor-pointer rounded-md border-2 border-gray-300 bg-white p-1 [color-scheme:light]"
                      />
                      <input
                        type="text"
                        value={branding[field] || ''}
                        onChange={(e) => setBranding({ ...branding, [field]: e.target.value })}
                        className="min-w-0 flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-gray-200 bg-slate-50/80 p-4 space-y-5">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Email footer</h3>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    Every customer email ends with the standard sign-off (best regards and how to get help), then your optional
                    message and links (below), then the grey legal / company footer at the very bottom—which you can edit in the
                    section at the bottom of this box. Use normal words in the editors; the system stores safe HTML for sending.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Optional footer message</label>
                  <p className="text-xs text-gray-500 mb-2">
                    Shown directly under the sign-off, before the optional link row. Leave empty if you do not need extra wording.
                  </p>
                  <RichTextEditor
                    value={branding.extraFooterHtml || ''}
                    onChange={(html) => setBranding({ ...branding, extraFooterHtml: html })}
                    placeholder="e.g. Thank you for using PRO-found®. For urgent queries, contact us through your account."
                    contentMinHeightClass="min-h-[140px] sm:min-h-[160px]"
                    contentWhiteSpace="normal"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Optional footer links</label>
                    <button
                      type="button"
                      onClick={() => setBranding({ ...branding, footerItems: [...(branding.footerItems || []), emptyFooterItem()] })}
                      className="text-sm text-emerald-600 hover:underline"
                    >
                      + Add link
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Each row is the <strong>words people see</strong> and the web address. Lower &quot;Order&quot; numbers appear
                    further left in the email.
                  </p>
                  <div className="space-y-2">
                    {(branding.footerItems || []).map((item, idx) => (
                      <div key={idx} className="flex gap-2 items-center flex-wrap bg-white rounded-lg border border-gray-200 p-2">
                        <input
                          placeholder="Link text (e.g. Privacy policy)"
                          value={item.label || ''}
                          onChange={(e) => {
                            const next = [...(branding.footerItems || [])];
                            next[idx] = { ...next[idx], label: e.target.value };
                            setBranding({ ...branding, footerItems: next });
                          }}
                          className="flex-1 min-w-[120px] border border-gray-200 rounded px-2 py-1.5 text-sm"
                        />
                        <input
                          type="url"
                          placeholder="https://…"
                          value={item.url || ''}
                          onChange={(e) => {
                            const next = [...(branding.footerItems || [])];
                            next[idx] = { ...next[idx], url: e.target.value };
                            setBranding({ ...branding, footerItems: next });
                          }}
                          className="flex-[2] min-w-[180px] border border-gray-200 rounded px-2 py-1.5 text-sm"
                        />
                        <input
                          type="number"
                          aria-label="Display order"
                          placeholder="Order"
                          value={item.order ?? 0}
                          onChange={(e) => {
                            const next = [...(branding.footerItems || [])];
                            next[idx] = { ...next[idx], order: Number(e.target.value) || 0 };
                            setBranding({ ...branding, footerItems: next });
                          }}
                          className="w-20 border border-gray-200 rounded px-2 py-1.5 text-sm"
                        />
                        <button
                          type="button"
                          className="text-red-600 text-sm shrink-0"
                          onClick={() => {
                            const next = (branding.footerItems || []).filter((_, i) => i !== idx);
                            setBranding({ ...branding, footerItems: next });
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-semibold text-gray-900">Legal / compliance footer (grey box)</label>
                  <p className="text-xs text-gray-600 mt-1 mb-2 leading-relaxed">
                    This is the company and privacy block at the very bottom of customer emails. It is pre-filled with the current
                    standard wording. Edit in plain language; use <strong>Insert data field</strong> for values that should follow
                    server settings (privacy URL, VAT number, company number, support email).
                  </p>
                  <RichTextEditor
                    value={branding.complianceFooterHtml || ''}
                    onChange={(html) => setBranding({ ...branding, complianceFooterHtml: html })}
                    placeholder="Company details and privacy notice…"
                    insertablePlaceholders={COMPLIANCE_FOOTER_INSERT_FIELDS}
                    contentMinHeightClass="min-h-[200px] sm:min-h-[240px]"
                    contentWhiteSpace="normal"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setBranding((b) => ({
                        ...b,
                        complianceFooterHtml: b.defaultComplianceFooterHtml || ''
                      }))
                    }
                    className="mt-2 text-sm text-emerald-700 hover:underline"
                  >
                    Reset legal footer to platform default template
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={saveBranding}
                disabled={brandingSaving}
                className="px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {brandingSaving ? 'Saving…' : 'Save branding'}
              </button>
            </>
          )}
        </div>
      )}

      {section === 'templates' && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <div className="space-y-4 max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notification type</label>
              <select
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {templateList.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label} ({t.key}){t.hasCustomTemplate ? ' *' : ''}
                    {t.emailEnabled === false ? ' — email off' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                * = saved custom template in the database. Changing type reloads the editor with that type&apos;s current template (or
                category starter if none saved). Reset clears your override.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
              <div className="min-w-0 sm:pr-2">
                <p className="text-sm font-medium text-gray-900">Send email for this notification type</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  When off, no email is sent for this type. In-app notifications may still appear if enabled in customer notification
                  settings.
                </p>
              </div>
              <div className="flex items-center justify-end gap-3 flex-none sm:justify-center sm:min-w-[7.5rem]">
                <span className="text-sm font-medium text-gray-800 tabular-nums w-8 text-right">{emailEnabled ? 'On' : 'Off'}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={emailEnabled}
                  aria-label={emailEnabled ? 'Email on — click to turn off' : 'Email off — click to turn on'}
                  disabled={emailEnabledSaving || templateLoading || !selectedKey}
                  onClick={toggleEmailEnabledForSelected}
                  className={`relative flex h-9 w-[3.25rem] min-w-[3.25rem] shrink-0 cursor-pointer items-center rounded-full border border-black/10 p-0.5 shadow-inner transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                    emailEnabled ? 'bg-emerald-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    aria-hidden
                    className={`pointer-events-none block h-7 w-7 rounded-full bg-white shadow-md ring-1 ring-black/5 transition-transform duration-200 ease-out ${
                      emailEnabled ? 'translate-x-[1.125rem]' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>

            {templateLoading ? (
              <p className="text-sm text-gray-500">Loading template…</p>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email subject</label>
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-stretch">
                    <input
                      value={subjectTpl}
                      onChange={(e) => setSubjectTpl(e.target.value)}
                      className="w-full min-w-0 flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <select
                      aria-label="Add field to subject line"
                      className="w-full sm:w-52 shrink-0 border border-gray-300 rounded-lg px-2 py-2 text-sm text-gray-700 bg-white"
                      value=""
                      onChange={(e) => {
                        const key = e.target.value;
                        e.target.value = '';
                        if (!key) return;
                        const token = `{{${key}}}`;
                        setSubjectTpl((prev) => {
                          const p = prev || '';
                          return p && !/\s$/.test(p) ? `${p} ${token}` : `${p}${token}`;
                        });
                      }}
                    >
                      <option value="">Add field to subject…</option>
                      {SUBJECT_INSERT_FIELDS.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Plain text in the inbox. Use the menu to insert dynamic fields, or type placeholders such as{' '}
                    <code className="bg-gray-100 px-1">{'{{title}}'}</code> yourself.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Email body (visual editor)</label>
                  <p className="text-xs text-gray-600">
                    Click <strong>+ Text box</strong> to add one green box, then click inside it and type — all your text
                    stays in that single box. Use <strong>Insert data field</strong> for automatic values (notification
                    title, message, etc.). The notification message box is locked so it cannot be split by accident.
                  </p>
                  <RichTextEditor
                    key={selectedKey}
                    value={innerHtml}
                    onChange={setInnerHtml}
                    placeholder="Click + Text box, then type inside the green box."
                    insertablePlaceholders={EMAIL_BODY_INSERT_FIELDS}
                    insertableBlocks={EMAIL_BODY_INSERT_BLOCKS}
                    emailBoxMode
                    contentMinHeightClass="min-h-[260px] sm:min-h-[320px]"
                    contentWhiteSpace="normal"
                  />

                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={includeActionInPreview}
                    onChange={(e) => setIncludeActionInPreview(e.target.checked)}
                  />
                  Include sample action button in preview
                </label>

                <div className="flex flex-wrap gap-2 pt-2">
                  {/* <button
                    type="button"
                    onClick={loadStarterForNotificationType}
                    className="px-4 py-2 rounded-lg border border-emerald-300 text-emerald-900 text-sm hover:bg-emerald-50"
                  >
                    Load starter for this type
                  </button> */}
                  {/* <button
                    type="button"
                    onClick={loadGenericDefaultIntoEditor}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                  >
                    Load generic default
                  </button> */}
                  <button
                    type="button"
                    onClick={() => refreshPreview()}
                    className="px-4 py-2 rounded-lg bg-gray-800 text-white text-sm hover:bg-gray-900"
                  >
                    Refresh preview now
                  </button>
                  <button
                    type="button"
                    onClick={saveTemplate}
                    disabled={templateSaving}
                    className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {templateSaving ? 'Saving…' : 'Save template'}
                  </button>
                  <button
                    type="button"
                    onClick={resetTemplate}
                    className="px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm hover:bg-red-50"
                  >
                    Reset to default
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="xl:sticky xl:top-4 self-start">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Live preview
              {previewSubject ? (
                <span className="font-normal text-gray-500"> — Subject: {previewSubject}</span>
              ) : null}
            </p>
            <p className="text-xs text-gray-500 mb-2">Updates automatically a short moment after you stop typing.</p>
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm" style={{ minHeight: 420 }}>
              {previewHtml ? (
                <iframe
                  title="Email preview"
                  className="w-full h-[560px] border-0"
                  srcDoc={previewHtml}
                  sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
                />
              ) : (
                <p className="p-4 text-sm text-gray-500">Preview will appear here.</p>
              )}
            </div>
            <p className="text-xs text-amber-700 mt-2">
              Sample recipient and message are used here. Real emails use data from each event.
            </p>
          </div>
        </div>
      )}

      {section === 'reference-guide' && (
        <div className="max-w-4xl space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Placeholder Reference Guide</h3>
            <p className="text-sm text-gray-600">
              Use these placeholders in your email templates. They are automatically replaced with real values when emails are sent.
            </p>
          </div>

          {/* Email Body Placeholders */}
          <div className="rounded-xl border border-gray-200 bg-white">
            <div className="px-5 py-4 border-b border-gray-100">
              <h4 className="text-base font-semibold text-gray-900">Email Body</h4>
              <p className="text-xs text-gray-500 mt-0.5">Available in the email body editor. Insert via the &quot;Add field&quot; dropdown.</p>
            </div>
            <div className="px-5 py-3">
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-semibold text-gray-700">Placeholder</th>
                      <th className="px-4 py-2.5 text-left font-semibold text-gray-700">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {EMAIL_BODY_INSERT_FIELDS.map((f) => (
                      <tr key={f.key} className="hover:bg-gray-50/50">
                        <td className="px-4 py-2.5">
                          <code className="bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded text-xs font-mono">{`{{${f.key}}}`}</code>
                        </td>
                        <td className="px-4 py-2.5 text-gray-700">{f.label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Subject Line Placeholders */}
          <div className="rounded-xl border border-gray-200 bg-white">
            <div className="px-5 py-4 border-b border-gray-100">
              <h4 className="text-base font-semibold text-gray-900">Subject Line</h4>
              <p className="text-xs text-gray-500 mt-0.5">Available in the email subject line. Insert via the &quot;Add field to subject&quot; dropdown.</p>
            </div>
            <div className="px-5 py-3">
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-semibold text-gray-700">Placeholder</th>
                      <th className="px-4 py-2.5 text-left font-semibold text-gray-700">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {SUBJECT_INSERT_FIELDS.map((f) => (
                      <tr key={f.key} className="hover:bg-gray-50/50">
                        <td className="px-4 py-2.5">
                          <code className="bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded text-xs font-mono">{`{{${f.key}}}`}</code>
                        </td>
                        <td className="px-4 py-2.5 text-gray-700">{f.label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Compliance Footer Placeholders */}
          <div className="rounded-xl border border-gray-200 bg-white">
            <div className="px-5 py-4 border-b border-gray-100">
              <h4 className="text-base font-semibold text-gray-900">Compliance Footer</h4>
              <p className="text-xs text-gray-500 mt-0.5">Available in the legal/compliance footer editor. Insert via the &quot;Insert data field&quot; dropdown.</p>
            </div>
            <div className="px-5 py-3">
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-semibold text-gray-700">Placeholder</th>
                      <th className="px-4 py-2.5 text-left font-semibold text-gray-700">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {COMPLIANCE_FOOTER_INSERT_FIELDS.map((f) => (
                      <tr key={f.key} className="hover:bg-gray-50/50">
                        <td className="px-4 py-2.5">
                          <code className="bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded text-xs font-mono">{`{{${f.key}}}`}</code>
                        </td>
                        <td className="px-4 py-2.5 text-gray-700">{f.label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesTab;
